import {
    NorthamptonshireRecordType,
    NorthamptonshireRecordSubType
} from "./NorthantsRecordTypes";
import { IStyleFactory } from "esdm-map/dist/layers/vector/styles/IStyleFactory";
import Style, { StyleFunction } from "ol/style/Style";
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import RegularShape from 'ol/style/RegularShape';
import Feature from 'ol/Feature';
import * as olHas from 'ol/has';

export class HbsmrStyleFactory implements IStyleFactory {

    public getStyle(recordType: string, recordSubType: string = null): Style | Style[] | StyleFunction
    {
        let style: Style | Style[] | StyleFunction = null;

        //try and resolve sub type first
        if (recordSubType !== null) {
            switch (recordSubType) {
                case NorthamptonshireRecordSubType.ConservationArea:
                    style = this.ConservationArea();
                    break;

                case NorthamptonshireRecordSubType.ListedBuilding:
                    style = this.ListedBuilding();
                    break;

                case NorthamptonshireRecordSubType.ScheduledMonument:
                    style = this.ScheduledMonument();
                    break;
            
                case NorthamptonshireRecordSubType.RegisteredParksAndGardens:
                    style = this.RegisteredParksAndGardens();
                    break;

                case NorthamptonshireRecordSubType.RegisteredBattlefields:
                    style = this.RegisteredBattlefields();
                    break;

                case NorthamptonshireRecordSubType.LocallyListedBuilding:
                    style = this.LocallyListedBuilding();
                    break;

            }

        }

        if (style === null) {
            switch (recordType) {
                case NorthamptonshireRecordType.Monument:
                    style = this.Monument();
                    break;

                case NorthamptonshireRecordType.Event:
                    style = this.Event();
                    break;

                case NorthamptonshireRecordType.Designation:
                    style = this.Designation();
                    break;
            }
        }

        return style;
    }

    public getSelectedStyle(recordType: NorthamptonshireRecordType): Style | Style[] | StyleFunction {
        let style: Style | Style[] | StyleFunction = null;

        switch (recordType) {
            case NorthamptonshireRecordType.Monument:
                style = this.MonumentSelected();
                break;

            case NorthamptonshireRecordType.Event:
                style = this.EventSelected();
                break;

            case NorthamptonshireRecordType.Designation:
                style = this.DesignationSelected();
                break;
        }

        return style;
    }

    private CommonStyle(fillColour: string): Style[] {
        let fill = new Fill({
            color: fillColour
        });
        let stroke = new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 3
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: stroke,
                    radius: 12
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }


    private Monument(): StyleFunction {
        return function (feature: Feature) {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    let subtypecode = feature.get('recsubtypecode');
                    // Monument – Circle - also used as default
                    let iconChr: string = '\uf111';
                    switch (subtypecode) {
                        //Building – Building
                        case 'BLD':
                            iconChr = '\uf1ad';
                            break;

                        //Findspot – Star
                        case 'FS':
                            iconChr = '\uf005';
                            break;

                        //Landscape – Trees
                        case 'LND':
                            iconChr = '\uf724';
                            break;

                        //Hedgerow – Tree-deciduous
                        case 'HED':
                            iconChr = '\uf400';
                            break;

                        //Natural Feature – Mound
                        case 'NAT':
                            iconChr = '\ue52d';
                            break;

                        //Place – Thumbtack
                        case 'PLA':
                            iconChr = '\uf08d';
                            break;
                    }

                    let symbol = new Text({
                        text: iconChr,
                        font: '900 18px "Font Awesome 6 Pro"',
                        textBaseline: 'bottom',
                        offsetY: 11,
                        fill: new Fill({
                            color: 'rgba(255,0,0,1)'
                        }),
                        stroke: new Stroke({
                            color: 'rgba(255,255,255,0.8)',
                            width: 3
                        })
                    });
                    return [new Style({ text: symbol })];
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255,0,0, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(255,0,0, 1)' })
                    });
            }
        }
    }

    private Event(): Style[] {
        //return this.CommonStyle('rgba(241, 171, 0, 0.4)');    
        let fill = new Fill({
            color: 'rgba(0, 0, 255, 0.4)'
        });
        let stroke = new Stroke({
            color: 'rgba(0, 0, 255, 1)',
            width: 2
        });
        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(0, 0, 255, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private Designation(): Style[] {
        return this.CommonStyle('rgba(132, 71, 15,0.4)');
    }

    private ListedBuilding(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\uf312',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(255, 173, 50, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255, 173, 50, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(255, 173, 50, 1)' })
                    });
            }
        };
    }

    private ConservationArea(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\uf3c5',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(255, 255, 0, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255, 255, 0, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(211, 211, 0, 1)', width:2 })
                    });
            }
        };
    }

    private ScheduledMonument(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\uf3c5',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(255, 0, 255, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255, 0, 255, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(255, 0, 255, 1)' })
                    });
            }
        };
    }

    private LocallyListedBuilding(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\ue5d6',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(255, 127, 0, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255, 127, 0, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(255, 127, 0, 1)' })
                    });
            }
        };
    }
    
    private RegisteredParksAndGardens(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\uf3c5',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(0, 255, 0, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(0, 255, 0, 0.5)' }),
                        stroke: new Stroke({ color: 'rgba(0, 255, 0, 1)' })
                    });
            }
        };
    }

    private RegisteredBattlefields(): StyleFunction {
        return function (feature: Feature): Style {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    return new Style({
                        text: new Text({
                            text: '\uf3c5',
                            font: '900 16px "Font Awesome 6 Pro"',
                            textBaseline: 'bottom',
                            offsetY: 6,
                            fill: new Fill({ color: 'rgba(168, 80, 255, 1)' }),
                            stroke: new Stroke({ color: 'rgba(50,50,50, 1)' })
                        })
                    });
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(168, 80, 255, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(168, 80, 255, 1)' })
                    });
            }
        };
    }

    private Selected(): Style[] {
        let fill = new Fill({
            color: 'rgba(63, 211, 0, 0.6)'
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            })
        ];
    }
    
    private MonumentSelected(): StyleFunction {
        return function (feature: Feature) {
            switch (feature.getGeometry().getType()) {
                case 'Point':
                    let subtypecode = feature.get('recsubtypecode');
                    // Monument – Circle - also used as default
                    let iconChr: string = '\uf111';
                    switch (subtypecode) {
                        //Building – Building
                        case 'BLD':
                            iconChr = '\uf1ad';
                            break;

                        //Findspot – Star
                        case 'FS':
                            iconChr = '\uf005';
                            break;

                        //Landscape – Trees
                        case 'LND':
                            iconChr = '\uf724';
                            break;

                        //Hedgerow – Tree-deciduous
                        case 'HED':
                            iconChr = '\uf400';
                            break;

                        //Natural Feature – Mound
                        case 'NAT':
                            iconChr = '\ue52d';
                            break;

                        //Place – Thumbtack
                        case 'PLA':
                            iconChr = '\uf08d';
                            break;
                    }

                    let symbol = new Text({
                        text: iconChr,
                        font: '900 18px "Font Awesome 6 Pro"',
                        textBaseline: 'bottom',
                        offsetY: 11,
                        fill: new Fill({
                            color: 'rgba(255,255,0,1)'
                        }),
                        stroke: new Stroke({
                            color: 'rgba(0,0,0,0.8)',
                            width: 5
                        })
                    });
                    return [new Style({ text: symbol })];
                default:
                    return new Style({
                        fill: new Fill({ color: 'rgba(255,255, 0, 0.4)' }),
                        stroke: new Stroke({ color: 'rgba(0, 0, 0, 1)' })
                    });
            }
        }

    }

    private EventSelected(): Style[] {
        return this.Selected();
    }

    private DesignationSelected(): StyleFunction {
        return function (feature: Feature): Style {
            let subtypecode = feature.get('recsubtypecode');
            let fill = new Fill({
                color: 'rgba(63, 211, 0, 0.6)'
            });
            let style = new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            });

            switch (subtypecode) {
                case 'LB':
                    switch (feature.getGeometry().getType()) {
                        case 'Point':
                            return new Style({
                                text: new Text({
                                    text: '\uf66f',
                                    font: '900 18px "Font Awesome 6 Pro"',
                                    textBaseline: 'bottom',
                                    offsetY: 6,
                                    fill: new Fill({ color: 'rgba(76, 255, 0, 1)' }),
                                    stroke: new Stroke({
                                        color: 'rgba(0,0,0,0.8)',
                                        width: 5
                                    })
                                })
                            });
                        default:
                            return style;
                    }
                default:
                    return style;
            }

        }
    }
}
